/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useState } from 'react';
import { scroller, Link } from 'react-scroll';
import parse from 'parse-json';
import { hooks as commonHooks, types as commonTypes } from '@sitebuilder/common';
import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import navbarStyles, { themeNavbarStyles } from './Navbar.styles';

const defaultReactScrollOptions = {
    spy: true,
    smooth: true,
    offset: -50,
    duration: 300,
    isDynamic: true,
};

interface SectionItem {
    id: string;
    type: string;
    label?: string;
    icon?: string;
    ariaLabel?: string;
}

interface WidgetProperties extends commonTypes.WidgetPropertiesBase {
    sections: SectionItem[];
    isResponsive: boolean;
    menuShowIcon?: string;
    menuCancelIcon?: string;
    scrollOptions?: string;
    typographyClass?: string;
    transformData?: boolean;
    functionToTransformData?: string;
}

interface IsRender {
    hideSections?: number[];
    widget?: true;
}

const Navbar = (props: commonTypes.WidgetInstanceProps) => {
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const {
        styles,
        sections,
        isResponsive,
        menuShowIcon,
        menuCancelIcon,
        scrollOptions,
        functionToTransformData,
        transformData,
    } = widgetProperties || props;
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { data } = props;
    const theme = data?.themeSelected || props.theme;
    const i18n = commonHooks.useLocalization();

    let isRender: IsRender = {
        hideSections: [],
        widget: true,
    };

    try {
        // eslint-disable-next-line no-new-func
        const transformFunction = Function(`${functionToTransformData}`)();
        const transformedProps = transformData && transformFunction(data);

        if (transformedProps) {
            isRender = { ...isRender, ...transformedProps };
        }
    } catch (e) {
        console.log(e);
    }

    // Options for React-scroll
    let options: any = defaultReactScrollOptions;

    try {
        options = { ...options, ...parse((scrollOptions as string) || '{}') };
    } catch (error) {
        options = { ...options };
    }

    const handleNavKeyPress = (event: any, targetId: string) => {
        if (event.key === 'Enter' || event.keyCode === 13) {
            scroller.scrollTo(targetId, { ...options });
        }
    };

    return isRender.widget ? (
        <nav
            id="navbar"
            className={`nav ${
                widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
            }`}
            css={css`
                ${!theme ? navbarStyles : themeNavbarStyles}${getTextSchemaProperties(
                    widgetProperties,
                    data?.colorSpace
                )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                    widgetProperties
                )}${styles || ''}
            `}
        >
            {/* Icon for responsive menu */}
            {isResponsive && (
                <button className="menu-icon" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                    <i className={isMenuOpen ? menuCancelIcon : menuShowIcon} />
                </button>
            )}
            <div
                className={`nav-items ${isMenuOpen ? 'show-menu' : 'hide-menu'}`}
                data-testid={'nav-items'}
            >
                {sections.map((s: SectionItem, i: number) =>
                    !isRender.hideSections?.includes(i) ? (
                        <Link
                            key={s.id}
                            data-key={s.id}
                            className={'nav-item'}
                            data-testid={'nav-item'}
                            activeClass={'active'}
                            to={s.id}
                            tabIndex={0}
                            onKeyPress={(e) => handleNavKeyPress(e, s.id)}
                            aria-label={i18n.format(s.ariaLabel || '', data)}
                            {...options}
                        >
                            {s.type === 'Label' ? (
                                i18n.format(s.label || '', data)
                            ) : (
                                <i className={`nav-icon ${s.icon}`} />
                            )}
                        </Link>
                    ) : (
                        <></>
                    )
                )}
            </div>
        </nav>
    ) : (
        <></>
    );
};

export default Navbar;
